import type { ComponentType } from 'react';
import type { RouterConfig } from 'react-aria-components';
import type { IconName } from '@launchpad-ui/icons';
import type { ZodType } from 'zod';

/**
 * Section configuration for grouping related commands.
 * Sections group commands by their inherent nature, not by registration context.
 */
interface Section {
  /** Unique identifier for the section */
  id: string;
  /** Human-readable label displayed as section header */
  label: string;
  /**
   * Optional priority used to bias section order. Both ranking
   * strategies compute a section's effective rank as
   * `max(section.priority, max(commandPriority within section))` so
   * either signal can elevate a bucket. Higher = ranks earlier;
   * insertion order is the tiebreak.
   */
  priority?: number;
}

/**
 * Static empty-state content rendered when a command is the entered
 * root and its child set is empty (e.g. an async fetch returned no
 * results, or the fetch is gated off).
 */
interface EmptyStateContent {
  label: string;
  description?: string;
  icon?: IconName;
}

/**
 * Source-declared empty state. Either a static content object, or a
 * function that derives content from the current query (so the empty-
 * query "teach the user what to type" form and the no-matches form
 * can render different copy from one declaration).
 */
type EmptyStateConfig = EmptyStateContent | ((ctx: { query: string }) => EmptyStateContent | undefined);

/**
 * Fields shared by every command shape. Identity (`id`), display
 * (`label` / `description` / `icon` / `shortcut` / `section`),
 * ranking (`priority`), tree (`parent`), search (`keywords`),
 * focused-entry (`snap`), and entered-empty-state (`emptyState`).
 */
interface BaseCommand {
  /** Unique identifier. Doubles as the registry key. */
  id: string;
  /** Human-readable label displayed in the listbox row. */
  label: string;
  /**
   * Optional short label used when this command appears as a parent
   * breadcrumb prefix for one of its children (e.g. a deep-search
   * hit surfaces a child from another scope). Falls back to `label`
   * when undefined.
   *
   * Useful when the parent's listbox label reads as a verb phrase
   * ("Search flags") but a shorter noun reads better in the
   * breadcrumb ("Flags > my-flag" vs "Search flags > my-flag").
   */
  breadcrumbLabel?: string;
  /** Optional supplementary text rendered under the label. */
  description?: string;
  /** Optional icon from @launchpad-ui/icons. */
  icon?: IconName;
  /**
   * Optional keyboard shortcut display string. Purely presentational;
   * binding lives in the keyboard-management hook.
   */
  shortcut?: string;
  /** Optional section grouping. Header rendered once per distinct id. */
  section?: Section;
  /**
   * Ranking hint. Higher = ranks earlier within its candidate set.
   * Tiebreak is stable insertion order. Defaults to 0.
   */
  priority?: number;
  /**
   * Optional parent command id. Makes this command a child of the
   * referenced command. Children are only visible when (a) their
   * parent is the entered root, or (b) the user is searching (deep
   * search across the whole tree). Throws an "orphan parent"
   * invariant at registration time if the referenced id isn't in
   * the registry.
   */
  parent?: string;
  /**
   * Optional prefix-key for `@`-style focused entry. When set, typing
   * `<prefix><snap> <query>` in the open palette sets `rootCommandId`
   * to this command and seeds the input with `<query>`. Snap is purely
   * opt-in -- there's no name-based fallback.
   */
  snap?: string;
  /**
   * Hidden text that participates in client-side fuzzy matching but
   * is never rendered. Two uses:
   *
   *   - Search aliases for static commands (the kbar pattern).
   *     `keywords: ['sign out']` so users typing "sign out" find a
   *     "Logout" command.
   *   - Exposing server-searchable fields on dynamic commands.
   *     Consumer fetches flags from an API filtering on name/key/desc;
   *     puts the key into `keywords` so Fuse sees the same surface
   *     the server matched on.
   */
  keywords?: string[];
  /**
   * Shown when this command is the entered root and its child set is
   * empty. Replaces the palette's generic "No matches" copy with
   * source-declared guidance. The function form receives the live
   * query so the empty-query (e.g. "Search flags by name, key, or
   * description...") and no-matches ("No flags match 'xyz'") cases
   * can render distinct copy from one declaration. Ignored when this
   * command is a leaf.
   */
  emptyState?: EmptyStateConfig;
}

/**
 * Action command. Two shapes:
 *
 *   - **Leaf**: provides `run`. Activation executes the callback
 *     and closes the palette.
 *   - **Scope**: omits `run`. Activation enters the command as a
 *     root via `openRoot`, regardless of whether children are
 *     registered yet. Children may register lazily via a separate
 *     hook gated on `rootCommandId`; the `emptyState` field covers
 *     the no-children-yet UX inside the scope.
 *
 * Scope-vs-leaf is decided by the presence of `run`, not by a
 * runtime check on the registry. This is the structural difference
 * from kbar, which expects children to be registered eagerly.
 *
 * Created via the `action()` factory.
 */
interface ActionCommand extends BaseCommand {
  type: 'action';
  /**
   * Optional callback invoked when the command is activated and has
   * no children. Container-vs-leaf is a runtime check on the resolved
   * store, not a type discriminant -- this matches kbar's rule.
   */
  run?: () => void | Promise<void>;
  /**
   * Optional content-loading status, mirroring React Query's
   * `QueryStatus` shape but defined here so the palette stays free
   * of any async-library coupling. Set this on an action whose
   * content (typically its children) is being loaded async; the
   * palette aggregates `'pending'` across the registry to drive the
   * search field's progress indicator. Omitted means "no
   * outstanding async work" -- equivalent to `'success'` for the
   * purpose of the aggregate spinner.
   *
   * Canonical use: a parent whose children load via `useQuery`
   * propagates the query's `status` here. Children themselves can
   * carry their own `status` for nested async (e.g. an LLM-backed
   * tree where each node is independently resolving).
   *
   * Lives only on actions because links are resolved by
   * construction -- a link's `href` is always known. Pre-resolution
   * states are modelled as actions that get re-registered as links
   * once the destination is known.
   */
  status?: CommandStatus;
  /**
   * Optional source-declared copy shown in the listbox's empty slot
   * while this command is the entered root AND its `status` is
   * `'pending'`. Lets a scope explain WHAT it's loading rather than
   * showing the no-match copy from `emptyState` (which the user
   * would otherwise see during the brief window between typing and
   * the fetch resolving). Same shape as `EmptyStateContent`.
   *
   * Precedence inside an entered scope:
   *   1. `status === 'pending'` && `loadingState` defined -> render it
   *   2. resolved `emptyState`
   *   3. generic "No matches"
   *
   * Ignored when this command is not the entered root, or when
   * `status` isn't `'pending'`.
   */
  loadingState?: EmptyStateContent;
}

/**
 * Content-loading status of a command. Same shape as React Query's
 * `QueryStatus`, intentionally structurally identical so consumers
 * can pass `useQuery({...}).status` through directly; the type lives
 * in the palette package so the palette doesn't depend on any
 * particular async-data library. Custom async sources (effects,
 * generators, anything) can satisfy this contract.
 *
 *   - `'pending'`: the content backing this command is still being
 *     resolved (the user should see a loading indicator).
 *   - `'error'`: the resolution failed. The palette doesn't render a
 *     dedicated UI for this today -- consumers handle error UX via
 *     `emptyState` or by registering a fallback command.
 *   - `'success'`: the content is resolved. Equivalent to omitting
 *     the field.
 */
type CommandStatus = 'pending' | 'error' | 'success';

/**
 * Link command. Navigates to a URL when activated. Honours
 * `newTab` (opens via `window.open(href, '_blank')`) and
 * `routerOptions` (RAC router config).
 *
 * Created via the `link()` factory.
 */
interface LinkCommand extends BaseCommand {
  type: 'link';
  href: RouterConfig['href'];
  routerOptions?: RouterConfig['routerOptions'];
  newTab?: boolean;
}

/**
 * View command. Swaps the palette listbox for a caller-provided React
 * subtree while the palette continues to own the modal shell, header
 * (Back + optional Learn more), and footer.
 *
 * Created via the `view<TPrefill>()` factory, which captures a
 * pre-typed `useView()` method so the view body reads its palette
 * api (`close` / `back` / `prefill`) without re-declaring `TPrefill`.
 */
interface ViewCommand<TPrefill = undefined> extends BaseCommand {
  type: 'view';
  /**
   * Display name shown in the view's header chrome. Usually a noun
   * phrase describing the view (e.g. "Send feedback", "Create flag").
   */
  name: string;
  /** Optional URL for the "Learn more" link in the view header. */
  learnMoreUrl?: string;
  /**
   * Component rendered as the view body. Receives no props -- consume
   * the palette context via this view's own `.useView()` method
   * (captured by the factory).
   *
   * Define at module scope so React preserves view-internal state
   * across palette re-renders.
   */
  Component: ComponentType;
  /**
   * Optional Zod schema describing the prefill payload this view
   * accepts when opened programmatically via `actions.openView(cmd,
   * prefill)`. Doubles as runtime validator at the open boundary and
   * type witness that flows `TPrefill` through to the view body via
   * `command.useView().prefill`.
   */
  prefillSchema?: ZodType<TPrefill>;
  /**
   * Pre-typed hook for the view's Component to read its palette api.
   * Captured at factory time so `TPrefill` flows from the
   * `view<TPrefill>()` call site to the Component body without a
   * second annotation.
   */
  useView: () => ViewCommandApi<TPrefill>;
}

/**
 * Runtime API surface a view's Component consumes via the view
 * command's own `useView()` method. Kept deliberately small.
 */
interface ViewCommandApi<TPrefill = undefined> {
  /** Close the entire palette. */
  close: () => void;
  /**
   * Return to main mode. The palette stays open and restores the
   * query that brought the user to this view, so Back feels like a
   * non-destructive step backwards.
   */
  back: () => void;
  /**
   * Structured prefill the view was opened with, validated against
   * `prefillSchema` at the boundary. `undefined` when the view
   * declares no schema or no prefill was passed. Stays constant for
   * the lifetime of the view.
   */
  prefill: TPrefill | undefined;
}

/**
 * Discriminated union of all command shapes.
 */
type Command = ActionCommand | LinkCommand | ViewCommand<unknown>;

/**
 * Resolve a command's `emptyState` against the current query. Returns
 * `undefined` if the command has no empty state declared or the
 * function form opts out (returns `undefined`).
 */
function resolveEmptyState(
  config: EmptyStateConfig | undefined,
  ctx: { query: string },
): EmptyStateContent | undefined {
  if (config === undefined) {
    return undefined;
  }
  if (typeof config === 'function') {
    return config(ctx);
  }
  return config;
}

export { resolveEmptyState };

export type {
  Section,
  BaseCommand,
  ActionCommand,
  LinkCommand,
  ViewCommand,
  ViewCommandApi,
  Command,
  CommandStatus,
  EmptyStateConfig,
  EmptyStateContent,
};
